/**
 * In-browser image compressor and optimizer.
 * Automatically resizes & compresses high-resolution images (such as wide banners & photos)
 * so they fit safely within reverse-proxy payloads (< 800 KB) without sacrificing HD clarity.
 */

export const optimizeImageForUpload = async (file, options = {}) => {
  const {
    maxSizeBytes = 800 * 1024, // 800 KB threshold (safely under Nginx 1MB default)
    maxDimension = 1920, // Max width or height (Full HD banner standard)
    initialQuality = 0.85 // High visual fidelity
  } = options;

  if (!file) return file;

  // Only optimize image mime types (ignore SVGs, videos, non-images)
  if (!file.type || !file.type.startsWith('image/')) {
    return file;
  }

  // Do not modify SVGs or animated GIFs
  if (file.type === 'image/svg+xml' || file.type === 'image/gif') {
    return file;
  }

  // If already under size limit, no compression needed
  if (file.size <= maxSizeBytes) {
    return file;
  }

  return new Promise((resolve) => {
    const reader = new FileReader();

    reader.onload = (event) => {
      const img = new Image();

      img.onload = () => {
        try {
          let { width, height } = img;

          // Scale down proportionally if either dimension exceeds maxDimension
          if (width > maxDimension || height > maxDimension) {
            if (width > height) {
              height = Math.round((height * maxDimension) / width);
              width = maxDimension;
            } else {
              width = Math.round((width * maxDimension) / height);
              height = maxDimension;
            }
          }

          const canvas = document.createElement('canvas');
          canvas.width = width;
          canvas.height = height;

          const ctx = canvas.getContext('2d');
          if (!ctx) {
            console.warn('[ImageOptimizer] Canvas context unavailable, using original file.');
            return resolve(file);
          }

          // Decide target format and extension
          const isPng = file.type === 'image/png';
          // Use WebP for PNGs to preserve potential transparency while compressing drastically,
          // otherwise JPEG for photos/banners.
          const targetMime = isPng ? 'image/webp' : 'image/jpeg';
          const targetExt = isPng ? '.webp' : '.jpg';

          // For JPEG, fill a white background in case of any transparent edges
          if (targetMime === 'image/jpeg') {
            ctx.fillStyle = '#FFFFFF';
            ctx.fillRect(0, 0, width, height);
          }

          ctx.drawImage(img, 0, 0, width, height);

          // Attempt compression with initialQuality
          canvas.toBlob(
            (blob) => {
              if (!blob) {
                return resolve(file);
              }

              // Build original name base
              const originalName = file.name || 'image';
              const dotIdx = originalName.lastIndexOf('.');
              const baseName = dotIdx > 0 ? originalName.substring(0, dotIdx) : originalName;
              const newFileName = `${baseName}${targetExt}`;

              const optimizedFile = new File([blob], newFileName, {
                type: blob.type || targetMime,
                lastModified: Date.now()
              });

              console.log(
                `[ImageOptimizer] Optimized "${file.name}" from ${(file.size / 1024).toFixed(1)} KB to ${(optimizedFile.size / 1024).toFixed(1)} KB (${width}x${height})`
              );

              resolve(optimizedFile);
            },
            targetMime,
            initialQuality
          );
        } catch (err) {
          console.warn('[ImageOptimizer] Compression error, falling back to original file:', err);
          resolve(file);
        }
      };

      img.onerror = (e) => {
        console.warn('[ImageOptimizer] Image load error, falling back to original file:', e);
        resolve(file);
      };

      img.src = event.target.result;
    };

    reader.onerror = (e) => {
      console.warn('[ImageOptimizer] FileReader error, falling back to original file:', e);
      resolve(file);
    };

    reader.readAsDataURL(file);
  });
};
